import { create } from 'zustand';
import { useAuthStore } from './auth';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

export interface ContentPiece {
  id: string;
  title: string;
  slug: string;
  type: 'article' | 'review' | 'comparison' | 'listicle' | 'guide';
  content: string | null;
  status: 'draft' | 'published' | 'archived';
  decayStatus: 'healthy' | 'warning' | 'critical' | 'unknown';
  primaryKeyword: string | null;
  metaDescription: string | null;
  pageViews7d: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContentInput {
  title: string;
  slug?: string;
  type: ContentPiece['type'];
  content?: string;
  primaryKeyword?: string;
  targetWordCount?: number;
}

export interface UpdateContentInput {
  title?: string;
  content?: string;
  status?: ContentPiece['status'];
  primaryKeyword?: string;
  metaDescription?: string;
}

interface ContentListResponse {
  success: boolean;
  data?: {
    content: ContentPiece[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
  error?: string;
}

interface ContentResponse {
  success: boolean;
  data?: {
    content: ContentPiece;
  };
  error?: string;
}

function getAuthHeaders(): HeadersInit {
  const { accessToken, tenant } = useAuthStore.getState();
  return {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
  };
}

interface ContentState {
  content: ContentPiece[];
  selectedContent: ContentPiece | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };

  // Actions
  fetchContent: (params?: { status?: string; limit?: number; offset?: number }) => Promise<void>;
  fetchContentById: (id: string) => Promise<ContentPiece | null>;
  createContent: (input: CreateContentInput) => Promise<ContentPiece | null>;
  updateContent: (id: string, input: UpdateContentInput) => Promise<ContentPiece | null>;
  deleteContent: (id: string) => Promise<boolean>;
  setSelectedContent: (content: ContentPiece | null) => void;
  clearError: () => void;
}

export const useContentStore = create<ContentState>()((set) => ({
  content: [],
  selectedContent: null,
  isLoading: false,
  error: null,
  pagination: { total: 0, limit: 20, offset: 0 },

  fetchContent: async (params): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      searchParams.set('limit', String(params?.limit ?? 20));
      searchParams.set('offset', String(params?.offset ?? 0));

      const res = await fetch(`${API_URL}/content?${searchParams.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      const data = (await res.json()) as ContentListResponse;

      if (!res.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to fetch content');
      }

      set({
        content: data.data?.content ?? [],
        pagination: data.data?.pagination ?? { total: 0, limit: 20, offset: 0 },
        isLoading: false,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch content',
      });
    }
  },

  fetchContentById: async (id: string): Promise<ContentPiece | null> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/content/${id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      const data = (await res.json()) as ContentResponse;

      if (!res.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to fetch content');
      }

      set({ selectedContent: data.data?.content ?? null, isLoading: false });
      return data.data?.content ?? null;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch content',
      });
      return null;
    }
  },

  createContent: async (input: CreateContentInput): Promise<ContentPiece | null> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/content`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      const data = (await res.json()) as ContentResponse;

      if (!res.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to create content');
      }

      const newContent = data.data?.content;
      if (newContent) {
        set((state) => ({
          content: [newContent, ...state.content],
          isLoading: false,
        }));
      }

      return newContent ?? null;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to create content',
      });
      return null;
    }
  },

  updateContent: async (id: string, input: UpdateContentInput): Promise<ContentPiece | null> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/content/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      const data = (await res.json()) as ContentResponse;

      if (!res.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to update content');
      }

      const updatedContent = data.data?.content;
      if (updatedContent) {
        set((state) => ({
          content: state.content.map((c) => (c.id === id ? updatedContent : c)),
          selectedContent: state.selectedContent?.id === id ? updatedContent : state.selectedContent,
          isLoading: false,
        }));
      }

      return updatedContent ?? null;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to update content',
      });
      return null;
    }
  },

  deleteContent: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_URL}/content/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      const data = (await res.json()) as { success: boolean; error?: string };

      if (!res.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to delete content');
      }

      set((state) => ({
        content: state.content.filter((c) => c.id !== id),
        selectedContent: state.selectedContent?.id === id ? null : state.selectedContent,
        isLoading: false,
      }));

      return true;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to delete content',
      });
      return false;
    }
  },

  setSelectedContent: (content): void => {
    set({ selectedContent: content });
  },

  clearError: (): void => {
    set({ error: null });
  },
}));
